import { addMonths, addYears, format } from 'date-fns';
import {
  ADD_ON_CODE,
  SUBSCRIPTION_CODE,
  SUBSCRIPTION_INTERVAL,
  SUBSCRIPTION_PLAN
} from 'src/types/enum';
import { formatAmount, formatNumber } from 'src/utils/numberFormat';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';

import type { BillingPlanType, UserStats } from './types';

/**
 * Surface tokens per card. The inverted card is expressed with design tokens
 * rather than a light/dark flag, so both follow the theme when dark mode lands.
 */
const LIGHT_SURFACE =
  'tw:bg-primary tw:border-border-primary tw:text-text-primary';
const DARK_SURFACE =
  'tw:bg-fg-secondary tw:border-border-secondary tw:text-text-over-dark';

export const PROFILE_ATTRIBUTES_MAX_LENGTH = 50;

export enum SETTINGS_TABS {
  SYSTEM_SETTINGS = 'system-settings',
  BILLING = 'billing',
  PROFILE = 'profile',
  NOTIFICATIONS = 'notifications',
  ORG_NOTIFICATIONS = 'organization-notifications',
  INSTRUCTIONS = 'instructions',
  EXTERNAL_API = 'external-api',
  MODEL_CONFIGURATION = 'model-configuration',
  PAY_AS_YOU_GO = 'pay-as-you-go',
  WORKFLOWS = 'workflows'
}

export const getUserStats = (
  dataLibraryStats: any[],
  dataChatStats: any,
  dataAssistantsStats: any[]
): UserStats => {
  const documentCount = dataLibraryStats?.reduce(
    (acc, curr) =>
      curr.name === T.F_S_PR || curr.name === T.F_S_RE || curr.name === T.F_S_US
        ? acc + curr.count
        : acc,
    0
  );
  const questionsCount = dataChatStats?.queries?.reduce(
    (acc, curr) => curr.count + acc,
    0
  );
  const assistantsCount = dataAssistantsStats?.[0].count;
  return { documentCount, questionsCount, assistantsCount };
};

export const billingPlans: BillingPlanType[] = [
  // {
  //   key: SUBSCRIPTION_PLAN.TRIAL,
  //   title: T.trial,
  //   subtitle: T.freeTrial,
  //   price: '0',
  //   description: T.trialCardText,
  //   button: { text: T.currentPlan, variant: 'outlined', disabled: true },
  //   features: [
  //     T.trialLi1,
  //     T.trialLi2,
  //     T.trialLi3,
  //     T.trialLi4,
  //     T.trialLi5,
  //     T.trialLi6
  //   ],
  //   cardStyles: { border: '1px solid #e5e7eb', color: 'inherit' }
  // },

  {
    key: SUBSCRIPTION_PLAN.ESSENTIALS,
    code: SUBSCRIPTION_CODE.ESSENTIALS,
    title: T.essentials,
    subtitle: T.essentialTagline,
    price: 0,
    afterPrice: T.perMonthPerUser,
    description: T.essentialCardText,
    button: {
      text: T.upgrade
    },
    featuresHeading: T.essentialFeaturesHeading,
    features: [
      T.essentialLi1,
      T.essentialLi2,
      T.essentialLi3,
      T.essentialLi4,
      T.essentialLi5,
      T.essentialLi6
    ],
    showPrice: true,
    surfaceClass: LIGHT_SURFACE,
    eyebrowClass: 'tw:text-icon-brand',
    // Essential is no longer sold. Protected by getVisibleBillingPlans().
    isHidden: true
  },
  {
    key: SUBSCRIPTION_PLAN.PROFESSIONAL,
    code: SUBSCRIPTION_CODE.PROFESSIONAL,
    title: T.professional,
    headline: T.professionalPlanHeadline,
    subtitle: T.professionalPlanSubheading,
    price: 0,
    afterPrice: T.perUserPerMonthExclVat,
    description: T.professionalPlanDescription,
    footnote: T.professionalPlanFootnote,
    button: {
      text: T.professionalPlanCta
    },
    features: [
      T.professionalFeature1,
      T.professionalFeature2,
      T.professionalFeature3,
      T.professionalFeature4,
      T.professionalFeature5,
      T.professionalFeature6
    ],
    showPrice: true,
    showBillingToggle: true,
    surfaceClass: LIGHT_SURFACE,
    eyebrowClass: 'tw:text-icon-brand'
  },
  {
    key: SUBSCRIPTION_PLAN.ENTERPRISE,
    code: SUBSCRIPTION_CODE.ENTERPRISE,
    title: T.enterprise,
    headline: T.enterprisePlanHeadline,
    subtitle: T.enterprisePlanSubheading,
    price: T.letsTalk,
    description: T.enterprisePlanDescription,
    footnote: T.enterprisePlanFootnote,
    button: {
      text: T.enterprisePlanCta,
      className: 'tw:bg-primary tw:text-text-primary tw:hover:bg-primary-hover'
    },
    features: [
      T.enterpriseFeature1,
      T.enterpriseFeature2,
      T.enterpriseFeature3,
      T.enterpriseFeature4,
      T.enterpriseFeature5
    ],
    surfaceClass: DARK_SURFACE,
    // The inverted card dims its eyebrow instead of tinting it brand blue,
    eyebrowClass: 'tw:opacity-60'
  }
];

const WORKFLOW_SHARED_FEATURES = [
  T.workflowAddOnFeatureUsers,
  T.workflowAddOnFeatureBlocks,
  T.workflowAddOnFeatureHistory,
  T.workflowAddOnFeaturePayg
];

/**
 * Workflow add-on tiers, rendered with the same PlanCard as the subscription
 * plans. The Professional tier ships with the Professional plan, so it has no
 * call to action.
 */
export const workflowAddOnPlans: BillingPlanType[] = [
  {
    key: 'workflow-professional' as SUBSCRIPTION_PLAN,
    code: undefined,
    title: T.workflowAddOnBasic,
    subtitle: T.workflowAddOnIncludedInProfessional,
    price: 0,
    description: '',
    button: { text: '' },
    features: [T.workflowAddOnProfessionalRuns, ...WORKFLOW_SHARED_FEATURES],
    surfaceClass: LIGHT_SURFACE,
    eyebrowClass: 'tw:text-icon-brand'
  },
  {
    key: 'workflow-enterprise' as SUBSCRIPTION_PLAN,
    code: undefined,
    title: T.workflowAddOnAdvanced,
    // Purchased through billing/add-ons; the Professional tier has no CTA.
    addOnCode: ADD_ON_CODE.WORKFLOW_RUNS,
    // Filled from GET billing/add-ons `addons[].price` by WorkflowAddOnCards.
    price: 0,
    subtitle: '',
    description: '',
    button: {
      text: T.workflowAddOnCta,
      className: 'tw:bg-primary tw:text-text-primary tw:hover:bg-primary-hover'
    },
    features: [T.workflowAddOnEnterpriseRuns, ...WORKFLOW_SHARED_FEATURES],
    showPrice: true,
    surfaceClass: DARK_SURFACE,
    eyebrowClass: 'tw:opacity-60'
  }
];

/**
 * A hidden plan is one we no longer sell, so new and trial tenants must never see
 * it. A tenant already subscribed to it still sees its card, which is what keeps
 * their "Current Plan" badge and their monthly -> yearly upgrade button working.
 */
export const getVisibleBillingPlans = (
  currentTierCode?: SUBSCRIPTION_CODE
): BillingPlanType[] =>
  billingPlans.filter(
    (plan) => !plan.isHidden || plan.code === currentTierCode
  );

export const currencySymbols: Record<string, string> = {
  CURRENCY_EUR: '€',
  CURRENCY_USD: '$',
  CURRENCY_GBP: '£'
};

export function formatPrice(amount: number, currency: string) {
  const symbol = currencySymbols[currency] || '€';
  return `${symbol} ${formatAmount(amount / 100)}`;
}

export function getPlanPrice(
  plan: any,
  billing: SUBSCRIPTION_INTERVAL,
  productPrices?: Record<string, any>,
  t?: any
) {
  // Add-on cards resolve their own price from GET billing/add-ons `addons[]`.
  if (plan.displayPrice) {
    return plan.displayPrice;
  }
  if (
    plan.key === SUBSCRIPTION_PLAN.ESSENTIALS ||
    plan.key === SUBSCRIPTION_PLAN.PROFESSIONAL
  ) {
    if (billing === SUBSCRIPTION_INTERVAL.YEARLY) {
      return formatPrice(
        productPrices?.[plan.key]?.yearly?.amount / 12,
        productPrices?.[plan.key]?.yearly?.currency
      );
    }
    return formatPrice(
      productPrices?.[plan.key]?.monthly?.amount,
      productPrices?.[plan.key]?.monthly?.currency
    );
  }

  return plan.key === SUBSCRIPTION_PLAN.ENTERPRISE
    ? t(plan.price)
    : `€ ${formatNumber(plan.price)}`;
}

export function getAutoRenewalDate(billingPeriod: SUBSCRIPTION_INTERVAL) {
  const now = new Date();
  let renewalDate;

  if (billingPeriod === SUBSCRIPTION_INTERVAL.MONTHLY) {
    renewalDate = addMonths(now, 1);
  } else if (billingPeriod === SUBSCRIPTION_INTERVAL.YEARLY) {
    renewalDate = addYears(now, 1);
  }

  return format(renewalDate, 'PPP');
}
